import React, { useCallback, useMemo } from 'react';

import {
	useClassNames,
} from '@common';

import {
	IconButton,
	Button,
} from '@components';

import {
	ArrowLeftIconBold,
	ArrowRightIconBold,
} from '@icons';

import './Pagination.scss';

interface PaginationProps {
	className?: string,

	currentPage: number,
	totalPagesCount: number,
	onPageChange: (page: number) => void,

	disabled?: boolean,
}

const MAX_PAGES_COUNT = 10;

function Pagination(props: PaginationProps): JSX.Element {
	const {
		totalPagesCount,
		onPageChange,
		currentPage,
		className,
		disabled,
	} = props;

	const visibleButtons = useMemo(() => {
		let
			startPage: number,
			endPage: number;

			if (totalPagesCount <= MAX_PAGES_COUNT) {
				startPage = 1;
				endPage = totalPagesCount;
			} else {
				if (currentPage <= 6) {
					startPage = 1;
					endPage = MAX_PAGES_COUNT;
				} else if (currentPage + 4 >= totalPagesCount) {
					startPage = totalPagesCount - 9;
					endPage = totalPagesCount;
				} else {
					startPage = currentPage - 5;
					endPage = currentPage + 4;
				}
			}

			const countOfVisibleButtons = (endPage + 1) - startPage;
			const result = [...Array(countOfVisibleButtons)].map((item, index) => startPage + index);

			return result;
	}, [totalPagesCount, currentPage]);

	const changePage = useCallback((newPage: number): void => {
		onPageChange(newPage);
	}, [onPageChange]);

	const goToPrevPage = useCallback((): void => {
		changePage(currentPage - 1);
	}, [changePage, currentPage]);

	const goToNextPage = useCallback((): void => {
		changePage(currentPage + 1);
	}, [changePage, currentPage]);

	const onChangePage = useCallback((newPage: number) => (): void => {
		changePage(newPage);
	}, [changePage]);

	const paginationButtonsView = useMemo(() => {
		return visibleButtons.map((page) => {
			const isSelectedPage = page === currentPage;
			const theme = isSelectedPage ? 'primary' : 'ghost';

			return (
				<Button
					key={page}
					theme={theme}
					disabled={disabled}
					className="page-button"
					onClick={onChangePage(page)}
				>
					{page}
				</Button>
			);
		});
	}, [visibleButtons, currentPage, disabled, onChangePage]);

	const prevPageButtonView = useMemo((): JSX.Element => {
		const disablePrevPageButton = currentPage < 2;

		return (!visibleButtons.length ? null :
			<IconButton
				disabled={disablePrevPageButton}
				icon={<ArrowLeftIconBold />}
				onClick={goToPrevPage}
				iconSize="12px"
				size="nogap"
				className="arrow"
			/>
		);
	}, [goToPrevPage, currentPage, visibleButtons.length]);

	const nextPageButtonView = useMemo(() => {
		const disableNextPageButton = currentPage >= Math.max(...visibleButtons);

		return (!visibleButtons.length ? null :
			<IconButton
				disabled={disableNextPageButton}
				icon={<ArrowRightIconBold />}
				onClick={goToNextPage}
				iconSize="12px"
				size="nogap"
				className="arrow"
			/>
		);
	}, [goToNextPage, currentPage, visibleButtons]);

	const classes = useClassNames('pagination', className);

	return (
		<div className={classes}>
			{prevPageButtonView}
			{paginationButtonsView}
			{nextPageButtonView}
		</div>
	);
}

export {Pagination};

/* eslint-disable no-debugger */
// debugger